import { createFileRoute, Outlet, redirect, useNavigate } from '@tanstack/react-router'
import { useEffect } from 'react'
import { AppShell } from '@/components/app/app-shell'
import { AuthShell } from '@/components/app/auth-shell'
import { useAppActions } from '@/components/app/use-app-actions'
import { Button } from '@/components/ui/button'
import { ForcePasswordChange } from '@/features/auth/force-password'
import { refreshSession } from '@/lib/api'
import { useSession } from '@/lib/session'

export const Route = createFileRoute('/_app')({
  beforeLoad: async ({ location }) => {
    if (useSession.getState().status === 'loading') await refreshSession()
    if (useSession.getState().status !== 'authenticated') {
      throw redirect({
        to: '/login',
        search: { redirect: location.href === '/' ? undefined : location.href },
      })
    }
  },
  component: AppLayout,
})

function AppLayout() {
  const status = useSession((s) => s.status)
  const memberships = useSession((s) => s.memberships)
  const isSuperadmin = useSession((s) => s.user?.isSuperadmin ?? false)
  const mustChangePassword = useSession((s) => s.user?.mustChangePassword ?? false)
  const { logout } = useAppActions()
  const navigate = useNavigate()
  // Si el refresh falla a mitad de uso (sesión revocada o vencida), volver al login.
  useEffect(() => {
    if (status === 'anonymous') navigate({ to: '/login', replace: true })
  }, [status, navigate])

  // Clave temporal: no se usa la aplicación hasta elegir una propia.
  if (status === 'authenticated' && mustChangePassword) return <ForcePasswordChange />

  // Sin ninguna compañía activa (suspendida o sin acceso) no hay nada que mostrar: se explica en vez de dejar la pantalla vacía.
  if (status === 'authenticated' && memberships.length === 0 && !isSuperadmin) {
    return (
      <AuthShell>
        <h1 className="font-semibold text-2xl tracking-tight">No tenés una compañía activa</h1>
        <p className="mt-2 text-muted-foreground text-sm">
          Tu compañía está suspendida o todavía no te dieron acceso a ninguna. Contactá a soporte para
          reactivarla.
        </p>
        <Button className="mt-6" variant="outline" onClick={() => logout()}>
          Cerrar sesión
        </Button>
      </AuthShell>
    )
  }

  return (
    <AppShell>
      <Outlet />
    </AppShell>
  )
}
